import React, { useEffect, useState } from 'react';
import Header from "../Header";
import Sidebar from "./SideBar";
import Content from '../Content';
import authStorage from '../../utilities/Storage';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  FiMessageSquare, FiSearch, FiFilter, FiDownload, FiEye, FiTrash2, 
  FiRefreshCw, FiChevronLeft, FiChevronRight, FiUser, FiCalendar, 
  FiClock, FiAlertCircle, FiCheckCircle, FiXCircle, FiTrendingUp
} from 'react-icons/fi';

export default function LogsPage() {
  const [email, setEmail] = useState(null);
  const [company_name, setCompany_Name] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'resolved', 'flagged'
  const [filterDate, setFilterDate] = useState('all'); // 'all', 'today', 'week', 'month'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // API base URL
  const API_URL = 'https://backend-livestream-production.up.railway.app/api';

  // Fetch conversations data from your API
  const fetchConversations = async () => {
  try {
    setRefreshing(true);
    setError(null);
    
    // Get user email from storage
    const userData = authStorage.getUser();
    let userEmail = null;
    
    if (userData) {
      try {
        const user = JSON.parse(userData);
        userEmail = user.email;
      } catch (error) {
        console.error("Failed to parse user data:", error);
      }
    }
    
    if (!userEmail) {
      throw new Error('User email not found. Please log in again.');
    }
    
    console.log("Fetching conversations for admin:", userEmail);
    
    // Fetch all conversations using the admin endpoint
    const response = await axios.get(`${API_URL}/admin/get-all-messages`, {
      params: {
        email: userEmail
      }
    });
    
    console.log("API response:", response.data);
    
    if (response.data && response.data.ok && response.data.data) {
      // Transform the data to match the expected format
      const transformedConversations = response.data.data.map(conv => {
        // Create a simple subject
        let subject;
        if (conv.type === 'community') {
          subject = 'Community Chat';
        } else {
          // For private chats, show the other participant's name
          const otherParticipant = conv.participants.find(p => p !== userEmail && p !== 'Community');
          subject = otherParticipant ? `Chat with ${otherParticipant}` : 'Private Chat';
        }
        
        return {
          id: conv.chat_id,
          participants: conv.participants,
          subject: subject,
          date: new Date(conv.lastMessage.timestamp).toLocaleDateString(),
          time: new Date(conv.lastMessage.timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          status: conv.lastMessage.status || 'active',
          messages: conv.messageCount,
          lastMessage: conv.lastMessage.message,
          flagged: conv.lastMessage.status === 'flagged',
          type: conv.type,
          messagesData: conv.messages
        };
      });
      
      setConversations(transformedConversations);
      setTotalPages(Math.ceil(transformedConversations.length / 10));
    } else {
      throw new Error(response.data.error || 'Failed to fetch conversations');
    }
  } catch (error) {
    console.error('Error fetching conversations:', error);
    
    // Provide more detailed error information
    let errorMessage = 'Failed to fetch conversations';
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
      errorMessage = `Server error: ${error.response.data.error || error.response.statusText}`;
    } else if (error.request) {
      console.error('Error request:', error.request);
      errorMessage = 'No response from server. Please check your connection.';
    } else {
      console.error('Error message:', error.message);
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    setConversations([]);
    setTotalPages(1);
  } finally {
    setLoading(false);
    setRefreshing(false);
  }
};

  function getUserRegistrationDetails() {
    const userData = authStorage.getUser();
    if (!userData) return;
    
    try {
      const user = JSON.parse(userData);
      setEmail(user.email);
      setCompany_Name(user.company_name);
    } catch (error) {
      console.error("Failed to parse user data:", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    getUserRegistrationDetails();
    fetchConversations();
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleRefresh = () => {
    fetchConversations();
  };

  const handleViewConversation = (conversation) => {
    setSelectedConversation(conversation);
    setShowViewModal(true);
  };

  const handleDeleteConversation = async (conversationId) => {
  if (window.confirm('Are you sure you want to delete this conversation?')) {
    try {
      const response = await axios.post(`${API_URL}/admin/delete-conversation`, {
        email: email,
        chat_id: conversationId
      });
      
      if (response.data && response.data.ok) {
        // Remove from local state
        setConversations(conversations.filter(conv => conv.id !== conversationId));
      } else {
        throw new Error(response.data.error || 'Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      setError(error.message || 'Failed to delete conversation');
    }
  }
};

  const handleFlagConversation = async (conversationId) => {
  const conversation = conversations.find(conv => conv.id === conversationId);
  const newStatus = conversation.flagged ? 'active' : 'flagged';
  
  try {
    const response = await axios.post(`${API_URL}/admin/update-conversation-status`, {
      email: email,
      chat_id: conversationId,
      status: newStatus
    });
    
    if (response.data && response.data.ok) {
      // Update local state
      setConversations(conversations.map(conv => 
        conv.id === conversationId ? { ...conv, flagged: !conv.flagged, status: newStatus } : conv
      ));
    } else {
      throw new Error(response.data.error || 'Failed to update conversation status');
    }
  } catch (error) {
    console.error('Error updating conversation status:', error);
    setError(error.message || 'Failed to update conversation status');
  }
};

  const handleResolveConversation = async (conversationId) => {
  try {
    const response = await axios.post(`${API_URL}/admin/update-conversation-status`, {
      email: email,
      chat_id: conversationId,
      status: 'resolved'
    });
    
    if (response.data && response.data.ok) {
      // Update local state
      setConversations(conversations.map(conv => 
        conv.id === conversationId ? { ...conv, status: 'resolved' } : conv
      ));
    } else {
      throw new Error(response.data.error || 'Failed to resolve conversation');
    }
  } catch (error) {
    console.error('Error resolving conversation:', error);
    setError(error.message || 'Failed to resolve conversation');
  }
};

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge bg-success bg-opacity-10 text-success">Active</span>;
      case 'resolved':
        return <span className="badge bg-secondary bg-opacity-10 text-secondary">Resolved</span>;
      case 'flagged':
        return <span className="badge bg-danger bg-opacity-10 text-danger">Flagged</span>;
      default:
        return <span className="badge bg-secondary bg-opacity-10 text-secondary">Unknown</span>;
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  // Filter conversations based on search term, status, and date
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.subject.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         conv.participants.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || conv.status === filterStatus;
    
    let matchesDate = true;
    const convDate = new Date(conv.date);
    const today = new Date();
    
    if (filterDate === 'today') {
      matchesDate = convDate.toDateString() === today.toDateString();
    } else if (filterDate === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = convDate >= weekAgo;
    } else if (filterDate === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      matchesDate = convDate >= monthAgo;
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Pagination
  const indexOfLastConversation = currentPage * 10;
  const indexOfFirstConversation = indexOfLastConversation - 10;
  const currentConversations = filteredConversations.slice(indexOfFirstConversation, indexOfLastConversation);

  return (
    <div className="app">
      {/* Mobile Header */}
      <div className="d-md-none mobile-header bg-white shadow-sm p-3 d-flex justify-content-between align-items-center">
        <button className="btn btn-outline-secondary" onClick={toggleMobileMenu}>
          <i className="bi bi-list"></i>
        </button>
        <h5 className="mb-0 fw-bold">Conversation Logs</h5>
        <button className="btn btn-outline-secondary" onClick={handleRefresh}>
          {refreshing ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <FiRefreshCw />
          )}
        </button>
      </div>

      {/* Sidebar with mobile toggle */}
      <div className={`sidebar-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Sidebar />
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={toggleMobileMenu}></div>
      )}

      <div className="main-content">
        {/* Regular Header (hidden on mobile) */}
        <div className="d-none d-md-block">
          <Header />
        </div>

        <Content>
          <motion.div 
            className="container-fluid py-4"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Page Header */}
            <motion.div variants={itemVariants} className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="fw-bold mb-1">Conversation Logs</h2>
                  <p className="text-muted mb-0">Monitor and manage user conversations</p>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-primary">
                    <FiDownload className="me-2" />Export Logs
                  </button>
                  <button className="btn btn-outline-primary" onClick={handleRefresh}>
                    {refreshing ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <FiRefreshCw />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Error Alert */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
              )}
            </motion.div>

            {/* Stats Cards */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-primary me-3">
                        <FiMessageSquare />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Total Conversations</h6>
                        <h3 className="fw-bold mb-0">{conversations.length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success">
                        <FiTrendingUp className="me-1" />12%
                      </span>
                      <span className="text-muted ms-2 small">vs last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-success me-3">
                        <FiCheckCircle />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Active</h6>
                        <h3 className="fw-bold mb-0">{conversations.filter(c => c.status === 'active').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success">
                        <FiClock className="me-1" />Ongoing
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-secondary me-3">
                        <FiCheckCircle />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Resolved</h6>
                        <h3 className="fw-bold mb-0">{conversations.filter(c => c.status === 'resolved').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-secondary bg-opacity-10 text-secondary">
                        <FiCheckCircle className="me-1" />Closed
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-danger me-3">
                        <FiAlertCircle />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Flagged</h6>
                        <h3 className="fw-bold mb-0">{conversations.filter(c => c.status === 'flagged').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-danger bg-opacity-10 text-danger">
                        <FiAlertCircle className="me-1" />Attention
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Search and Filter */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text bg-white border-end-0">
                            <FiSearch />
                          </span>
                          <input 
                            type="text" 
                            className="form-control border-start-0" 
                            placeholder="Search by subject or participant..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <select 
                          className="form-select" 
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="resolved">Resolved</option>
                          <option value="flagged">Flagged</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <select 
                          className="form-select" 
                          value={filterDate}
                          onChange={(e) => setFilterDate(e.target.value)}
                        >
                          <option value="all">All Time</option>
                          <option value="today">Today</option>
                          <option value="week">This Week</option>
                          <option value="month">This Month</option>
                        </select>
                      </div>
                      <div className="col-md-2">
                        <button className="btn btn-outline-secondary w-100">
                          <FiFilter className="me-2" />More Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Conversations Table */}
            <motion.div variants={itemVariants} className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold">All Conversations</h5>
                    <button className="btn btn-sm btn-outline-secondary">
                      <FiDownload className="me-1" />Export
                    </button>
                  </div>
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="d-flex justify-content-center align-items-center p-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : conversations.length === 0 ? (
                      <div className="text-center p-5">
                        <p className="text-muted">No conversations found.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">Subject</th>
                              <th scope="col">Participants</th>
                              <th scope="col">Date & Time</th>
                              <th scope="col">Messages</th>
                              <th scope="col">Status</th>
                              <th scope="col" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentConversations.map((conversation) => (
                              <tr key={conversation.id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {conversation.flagged && (
                                      <FiAlertCircle className="text-danger me-2" />
                                    )}
                                    <div>
                                      <h6 className="mb-0 fw-medium">{conversation.subject}</h6>
                                      <small className="text-muted">{conversation.lastMessage}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  {conversation.participants.map((participant, index) => (
                                    <span key={index}>
                                      {participant}
                                      {index < conversation.participants.length - 1 && ', '}
                                    </span>
                                  ))}
                                </td>
                                <td>
                                  <div>
                                    <div>{conversation.date}</div>
                                    <small className="text-muted">{conversation.time}</small>
                                  </div>
                                </td>
                                <td>{conversation.messages}</td>
                                <td>{getStatusBadge(conversation.status)}</td>
                                <td className="text-end">
                                  <div className="btn-group">
                                    <button 
                                      className="btn btn-sm btn-outline-secondary" 
                                      onClick={() => handleViewConversation(conversation)}
                                    >
                                      <FiEye />
                                    </button>
                                    {conversation.status === 'active' && (
                                      <button 
                                        className="btn btn-sm btn-outline-success" 
                                        onClick={() => handleResolveConversation(conversation.id)}
                                      >
                                        <FiCheckCircle />
                                      </button>
                                    )}
                                    <button 
                                      className={`btn btn-sm ${conversation.flagged ? 'btn-outline-danger' : 'btn-outline-warning'}`}
                                      onClick={() => handleFlagConversation(conversation.id)}
                                    >
                                      <FiAlertCircle />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-outline-danger" 
                                      onClick={() => handleDeleteConversation(conversation.id)}
                                    >
                                      <FiTrash2 />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="card-footer bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      Showing {indexOfFirstConversation + 1} to {Math.min(indexOfLastConversation, filteredConversations.length)} of {filteredConversations.length} conversations
                    </div>
                    <div className="d-flex gap-1">
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
                        disabled={currentPage === 1}
                      >
                        <FiChevronLeft />
                      </button>
                      {[...Array(totalPages)].map((_, i) => (
                        <button 
                          key={i} 
                          className={`btn btn-sm ${currentPage === i + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Content>
      </div>

      {/* View Conversation Modal */}
      {showViewModal && selectedConversation && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Conversation: {selectedConversation.subject}</h5>
                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Participants:</span>
                    <span>{selectedConversation.participants.join(', ')}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Status:</span>
                    <span>{getStatusBadge(selectedConversation.status)}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Started:</span>
                    <span>{selectedConversation.date} at {selectedConversation.time}</span>
                  </div>
                </div>
                
                <hr />
                
                <div className="conversation-messages" style={{ height: '400px', overflowY: 'auto' }}>
                  {selectedConversation.messagesData && selectedConversation.messagesData.length > 0 ? (
                    selectedConversation.messagesData.map((message, index) => (
                      <div 
                        key={index} 
                        className={`message ${message.sender_email === email ? 'user-message' : 'support-message'} mb-3`}
                      >
                        <div className={`d-flex ${message.sender_email === email ? 'justify-content-end' : 'justify-content-start'}`}>
                          <div className={`message-bubble ${message.sender_email === email ? 'bg-primary text-white' : 'bg-light'} p-3 rounded-3`} style={{ maxWidth: '70%' }}>
                            <div className="fw-medium small mb-1">{message.sender_name || 'Unknown'}</div>
                            <div>{message.message}</div>
                            <div className={`small ${message.sender_email === email ? 'text-white-50' : 'text-muted'} mt-1`}>
                              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-5">
                      <p className="text-muted">No messages in this conversation.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                {selectedConversation.status === 'active' && (
                  <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={() => {
                      handleResolveConversation(selectedConversation.id);
                      setShowViewModal(false);
                    }}
                  >
                    Mark as Resolved
                  </button>
                )}
                <button 
                  type="button" 
                  className={`btn ${selectedConversation.flagged ? 'btn-danger' : 'btn-warning'}`}
                  onClick={() => {
                    handleFlagConversation(selectedConversation.id);
                    setShowViewModal(false);
                  }}
                >
                  {selectedConversation.flagged ? 'Unflag' : 'Flag'} Conversation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="d-md-none mobile-bottom-nav fixed-bottom bg-white shadow-lg">
        <div className="d-flex justify-content-around">
          <button className="btn btn-link text-dark">
            <i className="bi bi-house-door-fill fs-5"></i>
            <small className="d-block">Dashboard</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-people fs-5"></i>
            <small className="d-block">Members</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-calendar-event fs-5"></i>
            <small className="d-block">Meetings</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-chat-dots-fill fs-5"></i>
            <small className="d-block">Logs</small>
          </button>
        </div>
      </div>

      <style jsx>{`
        .app {
          display: flex;
          min-height: 100vh;
          position: relative;
        }
        
        .sidebar-wrapper {
          width: 250px;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        
        .main-content {
          flex-grow: 1;
          padding-bottom: 60px; /* Space for mobile bottom nav */
        }
        
        .mobile-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
        }
        
        .mobile-bottom-nav {
          height: 60px;
          z-index: 1000;
        }
        
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
        }
        
        @media (max-width: 767.98px) {
          .sidebar-wrapper {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 1000;
            transform: translateX(-100%);
          }
          
          .sidebar-wrapper.mobile-open {
            transform: translateX(0);
          }
          
          .main-content {
            margin-left: 0;
          }
        }
        
        /* Stats Card Styles */
        .stats-card {
          transition: all 0.3s ease;
        }
        
        .stats-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
        }
        
        .stats-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }
        
        .stats-icon-primary {
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
        }
        
        .stats-icon-success {
          background: rgba(40, 167, 69, 0.1);
          color: #28a745;
        }
        
        .stats-icon-secondary {
          background: rgba(108, 117, 125, 0.1);
          color: #6c757d;
        }
        
        .stats-icon-danger {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }
        
        /* Table Styles */
        .table th {
          font-weight: 600;
          color: #495057;
          border-bottom: 1px solid #dee2e6;
        }
        
        /* Message Bubble Styles */
        .message-bubble {
          position: relative;
        }
        
        .message-bubble::before {
          content: '';
          position: absolute;
          top: 0;
          width: 0;
          height: 0;
          border: 10px solid transparent;
        }
        
        .user-message .message-bubble::before {
          right: -10px;
          border-left-color: #0d6efd;
          border-right: 0;
        }
        
        .support-message .message-bubble::before {
          left: -10px;
          border-right-color: #f8f9fa;
          border-left: 0;
        }
      `}</style>
    </div>
  )
}